import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "./SectionHead";
import { asset } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="border-t border-[#F5A524]/30 pt-4">
    <p data-testid={testid} className="font-display font-black text-5xl text-[#F5A524] leading-none">{String(value).padStart(2, "0")}</p>
    <p className="mt-2 font-mono text-[11px] tracking-[0.25em] text-zinc-400 uppercase">{label}</p>
  </div>
);

const Heading = ({ text }) => {
  const words = text.split(" ");
  const last = words.pop();
  return <>{words.join(" ")} <span className="text-[#F5A524]">{last}</span></>;
};

export const About = ({ index, projects, site }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const count = (c) => projects.filter((p) => p.category === c).length;
  const isDefault = !site?.about_image_url || site.about_image_url === "/img/street1.jpg";

  return (
    <section ref={ref} id="about" data-testid="about-section" className="relative px-5 sm:px-10 lg:px-16 py-16 md:py-24 border-t border-[#F5A524]/15 bg-[#0E0F14]">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal>
            <div className="relative clip-corner overflow-hidden aspect-[4/3] sm:aspect-[4/5] scanlines max-w-xl">
              <motion.img style={{ y: imgY }} src={asset(site?.about_image_url || "/img/street1.jpg")} alt="About the artist" data-testid="about-image"
                className={`w-full h-[116%] -mt-[8%] object-cover ${isDefault ? "grayscale contrast-125" : ""}`} />
              {isDefault && <div className="absolute inset-0 bg-[#F5A524] mix-blend-multiply opacity-80" />}
              <span className="absolute bottom-4 left-4 z-10 bg-black text-[#F5A524] font-mono text-[11px] tracking-[0.25em] px-3 py-2">ID // L.EMPORIO</span>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-10">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524] mb-4">// {index} — ABOUT</p>
            <h2 className="font-display font-black uppercase text-[2.75rem] sm:text-7xl leading-[0.88] tracking-tight">
              <Heading text={site?.about_heading || ""} />
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 md:mt-10 space-y-5 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-2xl">
              {(site?.about_paragraphs ?? []).map((p, i) => <p key={i} data-testid={`about-bio-${i}`}>{p}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 md:mt-14 grid grid-cols-3 gap-6 max-w-xl">
              <Stat label="Art pieces" value={count("art")} testid="stat-art" />
              <Stat label="Comics" value={count("comic")} testid="stat-comics" />
              <Stat label="Vignettes" value={count("vignette")} testid="stat-vignettes" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
